import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import './App.css';

// API endpoint - Change to your deployed backend URL in production
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Constants
const MAX_TEXT_LENGTH_FREE = 5000;
const MAX_TEXT_LENGTH_PRO = 50000;
const FREE_CONVERSIONS_LIMIT = 5;
const MAX_HISTORY_ITEMS = 10;
const MESSAGE_DISPLAY_DURATION = 4000;
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_MARGIN = 15;
const DEFAULT_TEXT_COLOR = '#000000';

function App() {
  // Core state
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [mode, setMode] = useState('dark');
  
  // PDF formatting state
  const [font, setFont] = useState('arial');
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [pageLayout, setPageLayout] = useState('portrait');
  const [marginSize, setMarginSize] = useState(DEFAULT_MARGIN);
  const [showLineNumbers, setShowLineNumbers] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState([]);
  
  // Freemium business model state
  const [isPro, setIsPro] = useState(false);
  const [conversionsRemaining, setConversionsRemaining] = useState(FREE_CONVERSIONS_LIMIT);
  const [totalConversions, setTotalConversions] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProFeatures, setShowProFeatures] = useState(false);

  // Computed values
  const characterCount = text.length;
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const currentMaxLength = isPro ? MAX_TEXT_LENGTH_PRO : MAX_TEXT_LENGTH_FREE;
  const pdfTitle = filename.trim();
  
  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedText = localStorage.getItem('pdfConverterText');
    const savedFilename = localStorage.getItem('pdfConverterFilename');
    const savedHistory = localStorage.getItem('downloadHistory');
    const savedIsPro = localStorage.getItem('isPro');
    const savedConversions = localStorage.getItem('conversionsRemaining');
    const savedTotalConversions = localStorage.getItem('totalConversions');
    
    if (savedText) setText(savedText);
    if (savedFilename) setFilename(savedFilename);
    if (savedHistory) setDownloadHistory(JSON.parse(savedHistory));
    if (savedIsPro) setIsPro(JSON.parse(savedIsPro));
    if (savedConversions) setConversionsRemaining(Number.parseInt(savedConversions, 10));
    if (savedTotalConversions) setTotalConversions(Number.parseInt(savedTotalConversions, 10));
  }, []);

  // Auto-save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('pdfConverterText', text);
    localStorage.setItem('pdfConverterFilename', filename);
    localStorage.setItem('isPro', JSON.stringify(isPro));
    localStorage.setItem('conversionsRemaining', conversionsRemaining.toString());
    localStorage.setItem('totalConversions', totalConversions.toString());
  }, [text, filename, isPro, conversionsRemaining, totalConversions]);

  // Toggle between dark and light modes
  const toggleMode = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  // Handle Stripe checkout
  const handleUpgradeToProClick = async () => {
    try {
      // Call backend to create checkout session
      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.error) {
        showError('Unable to start checkout. Please try again.');
        return;
      }

      // Redirect to Stripe Checkout
      // eslint-disable-next-line no-undef
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      showError('Payment system unavailable. Please try again later.');
    }
  };

  // Show error message temporarily
  const showError = (msg) => {
    setMessageType('error');
    setMessage(msg);
    setTimeout(() => setMessage(''), MESSAGE_DISPLAY_DURATION);
  };

  // Show success message temporarily
  const showSuccess = (msg) => {
    setMessageType('success');
    setMessage(msg);
    setTimeout(() => setMessage(''), MESSAGE_DISPLAY_DURATION);
  };

  const handleGeneratePDF = () => {
    // Validation: Check for empty text
    if (text.trim() === '') {
      showError('Text area is empty. Please enter some text.');
      return;
    }

    // Free tier limit validation
    if (!isPro) {
      if (text.length > MAX_TEXT_LENGTH_FREE) {
        setShowUpgradeModal(true);
        showError(`Free tier limited to ${MAX_TEXT_LENGTH_FREE} characters. Upgrade to Pro!`);
        return;
      }

      if (conversionsRemaining <= 0) {
        setShowUpgradeModal(true);
        showError('Free conversions exhausted. Upgrade to Pro for unlimited!');
        return;
      }
    }

    const orientation = pageLayout === 'landscape' ? 'l' : 'p';
    
    const doc = new jsPDF({
      format: 'a4',
      unit: 'mm',
      orientation: orientation,
    });

    // Add title if provided
    if (pdfTitle) {
      doc.setFontSize(16);
      doc.setFont(font, isBold ? 'bold' : 'normal');
      doc.text(pdfTitle, marginSize, marginSize);
      doc.setFontSize(fontSize);
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const textWidth = pageWidth - marginSize * 2;
    const startY = pdfTitle ? marginSize + 10 : marginSize;
    
    // Set text properties
    let fontStyle = 'normal';
    if (isBold) fontStyle = 'bold';
    else if (isItalic) fontStyle = 'italic';
    
    const r = Number.parseInt(DEFAULT_TEXT_COLOR.slice(1, 3), 16);
    const g = Number.parseInt(DEFAULT_TEXT_COLOR.slice(3, 5), 16);
    const b = Number.parseInt(DEFAULT_TEXT_COLOR.slice(5, 7), 16);
    doc.setFontSize(fontSize);
    doc.setFont(font, fontStyle);
    doc.setTextColor(r, g, b);

    // Split text into lines
    const lines = doc.splitTextToSize(text, textWidth);
    
    // Add line numbers if enabled
    let currentY = startY;
    const lineHeight = fontSize / 2.5;
    
    lines.forEach((line, index) => {
      if (currentY > pageHeight - marginSize) {
        doc.addPage();
        currentY = marginSize;
      }
      
      if (showLineNumbers) {
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text((index + 1).toString(), marginSize - 5, currentY);
        doc.setFontSize(fontSize);
        doc.setTextColor(r, g, b);
      }
      
      doc.text(line, marginSize, currentY);
      currentY += lineHeight;
    });

    const pdfName = filename || 'convertedText';
    doc.save(`${pdfName}.pdf`);

    // Add to download history (keep last 10)
    const newHistory = [
      { name: pdfName, date: new Date().toLocaleString() }, 
      ...downloadHistory
    ].slice(0, MAX_HISTORY_ITEMS);
    setDownloadHistory(newHistory);
    localStorage.setItem('downloadHistory', JSON.stringify(newHistory));

    // Update conversion tracking
    if (!isPro) {
      setConversionsRemaining(conversionsRemaining - 1);
    }
    setTotalConversions(totalConversions + 1);

    // Clear form and show success
    showSuccess('✨ PDF generated successfully!');
    setText('');
    setFilename('');
  };

  const handleClearText = () => {
    setText('');
    setFilename('');
  };

  // Read text file and update state
  const readTextFile = (file) => {
    if (file && file.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = (e) => setText(e.target.result);
      reader.readAsText(file);
    } else {
      showError('Please upload a valid .txt file.');
    }
  };

  const handleFileUpload = (event) => {
    readTextFile(event.target.files[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    readTextFile(event.dataTransfer.files[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <div className={`app-container ${mode}`}>
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div 
          className="modal-overlay" 
          onClick={() => setShowUpgradeModal(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowUpgradeModal(false)}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              onClick={() => setShowUpgradeModal(false)}
              aria-label="Close upgrade modal"
            >
              ✕
            </button>
            <h2 className="modal-title" id="upgrade-modal-title">🚀 Upgrade to Pro</h2>
            <p className="modal-description">Unlock unlimited conversions and premium features</p>
            
            <div className="pricing-grid">
              <div className="pricing-card free">
                <div className="pricing-badge">FREE</div>
                <h3>Free Tier</h3>
                <p className="pricing-price">$0</p>
                <ul className="pricing-features">
                  <li>✓ 5 conversions/month</li>
                  <li>✓ 5,000 character limit</li>
                  <li>✓ Basic fonts</li>
                  <li>✗ Custom fonts</li>
                  <li>✗ Batch conversion</li>
                </ul>
              </div>
              
              <div className="pricing-card pro active">
                <div className="pricing-badge">RECOMMENDED</div>
                <h3>Pro Plan</h3>
                <p className="pricing-price">$4.99<span>/month</span></p>
                <ul className="pricing-features">
                  <li>✓ Unlimited conversions</li>
                  <li>✓ 50,000 character limit</li>
                  <li>✓ All fonts</li>
                  <li>✓ Custom fonts</li>
                  <li>✓ Priority support</li>
                </ul>
                <button className="modal-button pro-button" onClick={handleUpgradeToProClick}>
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Free Tier Warning */}
      {!isPro && conversionsRemaining <= 2 && (
        <div className="warning-banner">
          <span>⚠️ {conversionsRemaining} free conversions remaining. <button onClick={() => setShowUpgradeModal(true)} className="link-button">Upgrade to Pro</button></span>
        </div>
      )}

      <header className="app-header">
        <div className="app-title">
          <div className="app-logo">📄</div>
          <div>
            <h1 className="app-heading">Text to PDF Converter {isPro && <span className="pro-badge">PRO</span>}</h1>
            <p className="app-subtitle">Fast, secure PDF generation for your documents</p>
          </div>
        </div>
        <button className="mode-toggle-button" onClick={toggleMode}>
          {mode === 'dark' ? '🌙' : '☀️'}
        </button>
      </header>

      {/* Pro Features Info */}
      {!isPro && (
        <button className="show-features-button" onClick={() => setShowProFeatures(!showProFeatures)}>
          {showProFeatures ? '⬆️ Hide Pro Features' : '⬇️ Show Pro Features'}
        </button>
      )}

      {showProFeatures && !isPro && (
        <div className="pro-features-banner">
          <h3>✨ Pro Features</h3>
          <ul>
            <li>🎨 Unlimited font customization</li>
            <li>📄 Process files up to 50,000 characters</li>
            <li>🔄 Batch conversion (multiple files)</li>
            <li>💾 Cloud backup for your documents</li>
            <li>🎯 Advanced formatting options</li>
            <li>🚀 Priority processing</li>
          </ul>
        </div>
      )}

      {/* Control Panel */}
      <div className="control-panel">
        <div className="control-row">
          <div className="control-group">
            <label htmlFor="font-select">Font: {!isPro && <span className="pro-feature-badge">PRO</span>}</label>
            <select id="font-select" value={font} onChange={(e) => setFont(e.target.value)} className="control-select" disabled={!isPro && font === 'arial' ? false : !isPro}>
              <option value="arial">Arial</option>
              <option value="times" disabled={!isPro}>Times New Roman {!isPro && '(Pro)'}</option>
              <option value="courier" disabled={!isPro}>Courier {!isPro && '(Pro)'}</option>
              <option value="helvetica" disabled={!isPro}>Helvetica {!isPro && '(Pro)'}</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="font-size">Size:</label>
            <input id="font-size" type="number" min="8" max="24" value={fontSize} onChange={(e) => setFontSize(Number.parseInt(e.target.value))} className="control-number" />
          </div>

          <div className="control-group">
            <label htmlFor="layout-select">Layout:</label>
            <select id="layout-select" value={pageLayout} onChange={(e) => setPageLayout(e.target.value)} className="control-select">
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="margin-size">Margin: mm</label>
            <input id="margin-size" type="number" min="5" max="30" value={marginSize} onChange={(e) => setMarginSize(Number.parseInt(e.target.value))} className="control-number" />
          </div>
        </div>

        <div className="control-row">
          <button className={`format-button ${isBold ? 'active' : ''}`} onClick={() => setIsBold(!isBold)} title="Bold">
            <strong>B</strong>
          </button>
          <button className={`format-button ${isItalic ? 'active' : ''}`} onClick={() => setIsItalic(!isItalic)} title="Italic">
            <em>I</em>
          </button>
          <button className={`format-button ${showLineNumbers ? 'active' : ''}`} onClick={() => setShowLineNumbers(!showLineNumbers)} title="Line Numbers">
            #
          </button>
        </div>
      </div>

      <input
        type="text"
        className="filename-input"
        placeholder="Enter file name (used for PDF title and filename)"
        value={filename}
        onChange={(e) => setFilename(e.target.value)}
      />
      
      <div className="textarea-wrapper">
        <div className="character-limit-indicator">
          <span>{text.length} / {currentMaxLength} characters</span>
          <div className="progress-bar">
            <div className="progress-fill" style={{width: `${(text.length / currentMaxLength) * 100}%`}}></div>
          </div>
        </div>
        <textarea
          rows="8"
          className="app-textarea"
          placeholder="Enter text here or drop a file below..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={currentMaxLength}
        />
      </div>
      
      <div className="file-upload-section">
        <label htmlFor="file-upload" className="file-upload-label">
          <span>Drag & Drop Text File</span> or <span className="browse-button">Browse</span>
        </label>
        <input
          type="file"
          id="file-upload"
          accept=".txt"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
      </div>
      
      <div
        className="drop-zone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <p>Drop your text file here</p>
      </div>
      
      <div className="stats-container">
        <span className="stat">
          <span>📊 Characters</span>
          <span>{characterCount}</span>
        </span>
        <span className="stat">
          <span>📝 Words</span>
          <span>{wordCount}</span>
        </span>
        <span className="stat">
          <span>🎯 Total Conversions</span>
          <span>{totalConversions}</span>
        </span>
        {!isPro && (
          <span className="stat remaining">
            <span>⚡ Remaining</span>
            <span>{conversionsRemaining}</span>
          </span>
        )}
      </div>
      
      <div className="button-group">
        <button className="app-button" onClick={handleGeneratePDF}>
          ✓ Convert to PDF
        </button>
        <button className="clear-button" onClick={handleClearText}>
          🗑️ Clear
        </button>
        {!isPro && (
          <button className="upgrade-button" onClick={handleUpgradeToProClick}>
            🚀 Go Pro
          </button>
        )}
      </div>

      {downloadHistory.length > 0 && (
        <div className="history-section">
          <h3>📋 Recent Downloads</h3>
          <ul className="history-list">
            {downloadHistory.map((item, historyIndex) => (
              <li key={`history_${item.name}_${item.date}`} className="history-item">
                {item.name}.pdf - {item.date}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <p className={`app-message ${messageType === 'error' ? 'error-message' : 'success-message'}`}>
        {message}
      </p>

      <footer className="app-footer">
        <span>© 2026 Text to PDF Converter</span>
        <span>Secure • Private • Fast PDFs</span>
      </footer>
    </div>
  );
}

export default App;
